import React, {
  useState,
  useCallback,
  useLayoutEffect,
  useEffect,
} from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Project, Measure, PlannedInvestment } from "@/types";
import { CategoryType } from "@/types/category";
import { getCategoryImageUrl } from "@/utils/getCategoryImageUrl";
import { getCategoryHexColor } from "@/utils/categoryColors";
import getProjectTranslation from "@/utils/getProjectTranslation";

import { ChevronLeft, ChevronRight, X } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { formatDateLong } from "@/utils/dateUtils";

interface UnifiedMeasure {
  id: number;
  project: Project;
  type: "implemented" | "planned";
  title: string;
  description: string;
  date: string;
  category?: CategoryType;
  // Additional properties from the original measures
  totalNet?: number;
  totalGross?: number;
  plannedBudget?: number;
  invoiceDate?: string;
  actionDate?: string;
  forestLocation?: string;
  invoiceNumber?: string;
  batchId?: string;
  batchTitle?: string;
}

interface CategoryDetailPageProps {
  category: CategoryType;
  projects: Project[];
  categoryData: Array<{
    category: CategoryType;
    displayName: string;
    description: string;
    totalMeasuresCount: number;
    color: string;
  }>;
  projectsMeasurements: Record<
    string,
    {
      planned: PlannedInvestment[];
      documented: Measure[];
    }
  >;
  onClose: () => void;
  onCategoryChange?: (category: CategoryType) => void;
  customerColor: string;
}

const CategoryDetailDesktopPage: React.FC<CategoryDetailPageProps> = ({
  category,
  projects,
  categoryData,
  projectsMeasurements,
  onClose,
  onCategoryChange,
  customerColor,
}) => {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState("implemented");
  const [scrollY, setScrollY] = useState(0);
  const modalRef = React.useRef<HTMLDivElement>(null);
  const hasNavigated = React.useRef(false);
  const isFirstOpen = React.useRef(true);

  // Initialize Embla Carousel
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "center",
    slidesToScroll: 1,
    startIndex: categoryData.findIndex((cat) => cat.category === category),
    skipSnaps: false,
    inViewThreshold: 0.7,
  });

  // Navigation functions
  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      hasNavigated.current = true;
      isFirstOpen.current = false; // Mark that we're no longer in first open because of the sliding animation
      emblaApi.scrollPrev(true);
    }
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      hasNavigated.current = true;
      isFirstOpen.current = false; // Mark that we're no longer in first open
      emblaApi.scrollNext(true);
    }
  }, [emblaApi]);

  // Handle carousel selection
  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const newIndex = emblaApi.selectedScrollSnap();
    const newCategory = categoryData[newIndex]?.category;
    if (onCategoryChange && newCategory && newCategory !== category) {
      onCategoryChange(newCategory);
    }
  }, [emblaApi, onCategoryChange, category, categoryData]);

  // Initialize carousel event listeners
  useEffect(() => {
    if (!emblaApi) return;

    emblaApi.on("select", onSelect);

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Handle keyboard navigation and prevent body scroll
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft") {
        hasNavigated.current = true;
        isFirstOpen.current = false; // Mark that we're no longer in first open
        scrollPrev();
      } else if (event.key === "ArrowRight") {
        hasNavigated.current = true;
        isFirstOpen.current = false; // Mark that we're no longer in first open
        scrollNext();
      } else if (event.key === "Escape") {
        onClose();
      }
    };

    // Prevent body scroll when modal is open
    document.body.style.overflow = "hidden";

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      // Restore body scroll when modal is closed
      document.body.style.overflow = "unset";
    };
  }, [scrollPrev, scrollNext, onClose]);

  // Handle scroll effect for smooth header shrinking
  useLayoutEffect(() => {
    const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement;
      const scrollTop = target.scrollTop;
      setScrollY(scrollTop);
    };

    // Wait for the next frame to ensure DOM is ready
    const attachScrollListener = () => {
      const modalContainer = modalRef.current;
      if (modalContainer) {
        modalContainer.addEventListener("scroll", handleScroll, {
          passive: true,
        });
        return () => {
          modalContainer.removeEventListener("scroll", handleScroll);
        };
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    const frameId = requestAnimationFrame(attachScrollListener);

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [category]);

  // Reset scroll position when category changes
  useLayoutEffect(() => {
    setScrollY(0);

    // Reset scroll position only when category changes
    const resetScroll = () => {
      const modalContainer = modalRef.current;
      if (modalContainer) {
        modalContainer.scrollTop = 0;
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    const frameId = requestAnimationFrame(resetScroll);

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [category]);

  // Get all measures for this category
  const allMeasures = projects.reduce((acc, project) => {
    const measurements = projectsMeasurements[project.id];
    if (!measurements) return acc;

    const { planned, documented } = measurements;

    // Add documented measures
    if (documented && documented.length > 0) {
      const categoryMeasures = documented.filter(
        (measure) => measure.category === category
      );
      categoryMeasures.forEach((measure) => {
        acc.push({
          ...measure,
          project,
          type: "implemented" as const,
          title: measure.description, // Use description as title for documented measures
          description: "", // No separate description field for documented measures
          date: measure.invoiceDate
            ? new Date(measure.invoiceDate).toISOString()
            : "",
        });
      });
    }

    // Add planned measures
    if (planned && planned.length > 0) {
      const categoryMeasures = planned.filter(
        (measure) => measure.category === category
      );
      categoryMeasures.forEach((measure) => {
        acc.push({
          ...measure,
          project,
          type: "planned" as const,
          title: measure.measure, // Use measure as title for planned measures
          description: measure.description,
          date: measure.actionDate || "",
        });
      });
    }

    return acc;
  }, [] as UnifiedMeasure[]);

  const implementedMeasures = allMeasures.filter(
    (measure) => measure.type === "implemented"
  );
  const plannedMeasures = allMeasures.filter(
    (measure) => measure.type === "planned"
  );

  // Get next category name for tooltip
  const getNextCategoryName = () => {
    const currentIndex = categoryData.findIndex(
      (cat) => cat.category === category
    );
    const nextIndex = (currentIndex + 1) % categoryData.length;
    return categoryData[nextIndex]?.displayName || "";
  };

  // Get previous category name for tooltip
  const getPreviousCategoryName = () => {
    const currentIndex = categoryData.findIndex(
      (cat) => cat.category === category
    );
    const previousIndex =
      currentIndex === 0 ? categoryData.length - 1 : currentIndex - 1;
    return categoryData[previousIndex]?.displayName || "";
  };

  // Update active tab when measures change
  useEffect(() => {
    if (
      implementedMeasures.length > 0 &&
      activeTab === "planned" &&
      plannedMeasures.length === 0
    ) {
      setActiveTab("implemented");
    } else if (
      plannedMeasures.length > 0 &&
      activeTab === "implemented" &&
      implementedMeasures.length === 0
    ) {
      setActiveTab("planned");
    }
  }, [implementedMeasures.length, plannedMeasures.length, activeTab]);

  // Helper function to render a category card
  const renderCategoryCard = (categoryType: CategoryType, key?: string) => {
    const catInfo = categoryData.find((cat) => cat.category === categoryType);
    const catImage = getCategoryImageUrl(categoryType);
    const catColor = getCategoryHexColor(categoryType);
    const isActive = categoryType === category;

    return (
      <div
        key={key}
        className="embla__slide_categoryCards flex-[0_0_80%] min-w-0 pl-4 pr-4 flex items-center"
      >
        <div
          ref={isActive ? modalRef : null}
          className={`bg-[#FAFAFA] rounded-2xl w-full overflow-y-auto transition-transform duration-300 ease-in custom-scrollbar ${
            isActive ? "h-[90vh] scale-100" : "h-[70vh] scale-90 opacity-70"
          }`}
        >
          {/* Header with image - smooth shrinking */}
          <div
            className={`relative ${
              isActive ? "sticky top-0 z-10 bg-[#FAFAFA]" : ""
            }`}
          >
            <div
              className="bg-cover bg-center relative transition-all duration-200 ease-out"
              style={{
                backgroundImage: `linear-gradient(to left, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.6) 54%, rgba(0, 0, 0, 0.95) 100%), url(${catImage})`,
                height: isActive
                  ? `${Math.max(86, 384 - scrollY * 1.2)}px`
                  : "384px",
              }}
            >
              {/* Title and description - overlaid on image */}
              <div className="absolute inset-0 flex items-center pl-[78px] text-white transition-all duration-200 ease-out">
                <div className="space-y-4 transition-all duration-200 ease-out">
                  {/* Color bar - fades out on scroll */}
                  <div
                    className="h-2 w-8 rounded-[9px] transition-all duration-200 ease-out"
                    style={{
                      backgroundColor: catColor,
                      opacity: isActive ? Math.max(0, 1 - scrollY / 100) : 1,
                      height: isActive
                        ? `${Math.max(0, 8 - scrollY / 25)}px`
                        : "8px",
                    }}
                  />

                  {/* Title - shrinks on scroll */}
                  <h1
                    className="font-semibold transition-all duration-200 ease-out"
                    style={{
                      fontSize: isActive
                        ? `${Math.max(16, 24 - scrollY * 0.2)}px`
                        : "24px",
                    }}
                  >
                    {catInfo?.displayName}
                  </h1>

                  {/* Description - fades out on scroll */}
                  <div
                    className="transition-all duration-200 ease-out"
                    style={{
                      opacity: isActive ? Math.max(0, 1 - scrollY / 80) : 1,
                      maxHeight: isActive
                        ? `${Math.max(0, 80 - scrollY * 0.5)}px`
                        : "80px",
                      overflow: "hidden",
                      width: "50%",
                      lineHeight: "1.4",
                    }}
                  >
                    <p className="text-md leading-relaxed">
                      {catInfo?.description}
                    </p>
                  </div>
                </div>
              </div>

              {isActive && (
                <div className="absolute top-7 right-7 flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={onClose}
                    className="bg-white hover:bg-white"
                    style={{
                      height: "32px",
                      width: "32px",
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Tabs - sticky */}
          {isActive &&
            (implementedMeasures.length > 0 || plannedMeasures.length > 0) && (
              <div className="pl-[78px] pr-[78px]">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <div
                    className="bg-white -ml-[78px] -mr-[78px] sticky z-10 transition-all duration-200 ease-out"
                    style={{
                      top: isActive
                        ? `${Math.max(86, 384 - scrollY * 1.2)}px`
                        : "0px",
                      opacity: isActive ? (scrollY > 50 ? 1 : 0.95) : 1,
                    }}
                  >
                    <TabsList className="w-full pl-[78px] pr-[78px]">
                      {implementedMeasures.length > 0 && (
                        <TabsTrigger
                          value="implemented"
                          activeColor={customerColor}
                        >
                          {t("categoryDetail.implementedMeasures")}
                        </TabsTrigger>
                      )}
                      {plannedMeasures.length > 0 && (
                        <TabsTrigger
                          value="planned"
                          activeColor={customerColor}
                        >
                          {t("categoryDetail.plannedMeasures")}
                        </TabsTrigger>
                      )}
                    </TabsList>
                  </div>

                  {implementedMeasures.length > 0 && (
                    <TabsContent value="implemented" className="mt-6">
                      <div className="space-y-4">
                        <p className="text-gray-500 w-1/2">
                          {t("categoryDetail.implementedMeasuresDescription")}
                        </p>
                        <div className="text-sm font-semibold text-gray-700 text-right">
                          {implementedMeasures.length}{" "}
                          {t("categoryDetail.implementedMeasures")}
                        </div>

                        <Card>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-2/4">
                                  {t("categoryDetail.measure")}
                                </TableHead>
                                <TableHead className="w-1/4">
                                  {t("categoryDetail.project")}
                                </TableHead>
                                <TableHead className="w-1/4 text-right">
                                  {t("categoryDetail.date")}
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {implementedMeasures.map((measure, index) => (
                                <TableRow key={index}>
                                  <TableCell className="font-medium align-top">
                                    {measure.title}
                                  </TableCell>
                                  <TableCell className="align-top">
                                    <Badge className="inline-block border-0 text-[#009982] py-1 px-2 rounded bg-[#E8F6F4] hover:bg-[#E8F6F4] my-[4px]">
                                      {getProjectTranslation(measure.project.nameTranslations, language) || measure.project.title}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="whitespace-nowrap text-right align-top">
                                    {formatDateLong(measure.date, language)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Card>
                        <div className="pb-12"></div>
                      </div>
                    </TabsContent>
                  )}

                  {plannedMeasures.length > 0 && (
                    <TabsContent value="planned" className="mt-6">
                      <div className="space-y-4">
                        <p className="text-gray-500 w-1/2">
                          {t("categoryDetail.plannedMeasuresDescription")}
                        </p>
                        <div className="text-sm font-semibold text-gray-700 text-right">
                          {plannedMeasures.length}{" "}
                          {t("categoryDetail.plannedMeasures")}
                        </div>

                        <Card>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-1/4">
                                  {t("categoryDetail.measure")}
                                </TableHead>
                                <TableHead className="w-1/3">
                                  {t("categoryDetail.description")}
                                </TableHead>
                                <TableHead className="w-1/4">
                                  {t("categoryDetail.project")}
                                </TableHead>
                                <TableHead className="w-1/6 text-right">
                                  {t("categoryDetail.date")}
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {plannedMeasures.map((measure, index) => (
                                <TableRow key={index}>
                                  <TableCell className="font-medium align-top">
                                    {measure.title}
                                  </TableCell>
                                  <TableCell className="align-top">
                                    {measure.description}
                                  </TableCell>
                                  <TableCell className="align-top">
                                    <Badge className="inline-block border-0 text-[#009982] py-1 px-2 rounded bg-[#E8F6F4] hover:bg-[#E8F6F4] my-[4px]">
                                      {getProjectTranslation(measure.project.nameTranslations, language) || measure.project.title}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="whitespace-nowrap text-right align-top">
                                    {formatDateLong(measure.date, language)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Card>
                        <div className="pb-12"></div>
                      </div>
                    </TabsContent>
                  )}
                </Tabs>
              </div>
            )}

          {/* No measures message */}
          {isActive &&
            implementedMeasures.length === 0 &&
            plannedMeasures.length === 0 && (
              <div className="pl-[78px] pr-[78px] mt-6">
                <div className="text-center py-12 text-gray-500">
                  <p className="text-lg mb-2">
                    {t("categoryDetail.noMeasuresTitle")}
                  </p>
                  <p>{t("categoryDetail.noMeasuresDescription")}</p>
                </div>
              </div>
            )}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="relative w-full">
        {/* Carousel Container */}
        <div
          className={`embla overflow-hidden ${
            isFirstOpen.current && !hasNavigated.current ? "no-transition" : ""
          }`}
          ref={emblaRef}
        >
          <div className="embla__container_categoryCards flex">
            {categoryData.map((categoryItem) =>
              renderCategoryCard(categoryItem.category, categoryItem.category)
            )}
          </div>
        </div>

        {/* Navigation Arrows - positioned relative to active card */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={scrollPrev}
                className="absolute top-1/2 transform -translate-y-1/2 z-10 bg-white hover:bg-white rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110"
                style={{ left: "calc(10% - 40px)" }}
                aria-label="Previous category"
              >
                <ChevronLeft className="w-6 h-6 text-gray-700" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{getPreviousCategoryName()}</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={scrollNext}
                className="absolute top-1/2 transform -translate-y-1/2 z-10 bg-white hover:bg-white rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110"
                style={{ right: "calc(10% - 40px)" }}
                aria-label="Next category"
              >
                <ChevronRight className="w-6 h-6 text-gray-700" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>{getNextCategoryName()}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
};

export default CategoryDetailDesktopPage;
